import React, { useState } from 'react';
import { format } from 'date-fns';
import { Edit, Trash2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import InvoiceStatusBadge from '../InvoiceStatusBadge';
import { Invoice } from '@/types/models';
import { getInvoiceLineItems } from '@/services/invoiceService';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import ProfessionalInvoiceView from '../ProfessionalInvoiceView';

interface GroupedInvoicesProps {
  groupedInvoices: Record<string, Invoice[]>;
  familyMap: Record<string, string>;
  onEditInvoice: (invoice: Invoice) => void;
  onDeleteInvoice: (invoice: Invoice) => void;
  formatAmount: (amountInCents: number) => string;
}

export const GroupedInvoices: React.FC<GroupedInvoicesProps> = ({
  groupedInvoices,
  familyMap,
  onEditInvoice,
  onDeleteInvoice,
  formatAmount,
}) => {
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  const handleViewInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
  };

  return (
    <div className="space-y-6">
      {Object.keys(groupedInvoices).length > 0 ? (
        Object.entries(groupedInvoices).map(([familyId, familyInvoices]) => (
          <Card key={familyId}>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">{familyMap[familyId]}</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice Number</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {familyInvoices.map((invoice) => (
                    <TableRow key={invoice.id}>
                      <TableCell className="font-medium">{invoice.invoice_number || 'PENDING'}</TableCell>
                      <TableCell>{formatAmount(invoice.total_cents)}</TableCell>
                      <TableCell>{format(new Date(invoice.due_date), 'MMM d, yyyy')}</TableCell>
                      <TableCell>
                        <InvoiceStatusBadge status={invoice.status} />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button 
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewInvoice(invoice)}
                          >
                            View Details
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => onEditInvoice(invoice)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => onDeleteInvoice(invoice)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ))
      ) : (
        <div className="text-center py-12 bg-muted/20 rounded-lg">
          <p className="text-muted-foreground">No invoices found.</p>
        </div>
      )}
      <ProfessionalInvoiceView
        open={!!selectedInvoice}
        onOpenChange={() => setSelectedInvoice(null)}
        invoice={selectedInvoice}
        organizationId={selectedInvoice?.organization_id || ''}
      />
    </div>
  );
};
